import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

/**
 * Prompt the server for a price + description (and for gift-cards, an
 * optional card code + recipient) whenever they tap an ``is_open`` menu
 * item. Not persistent — closes on Cancel or Add-to-Order.
 *
 * Contract with the caller:
 *   onConfirm({ price, custom_name, gift_card_code? })
 */
export default function OpenItemModal({ open, item, onClose, onConfirm }) {
  const kind = item?.open_kind || "food";
  const [priceStr, setPriceStr] = useState("");
  const [name, setName] = useState("");
  const [autoGen, setAutoGen] = useState(true);   // gift-card mode only
  const [cardCode, setCardCode] = useState("");
  const [error, setError] = useState("");

  // Reset every time the modal opens for a new item so state doesn't
  // leak from the previous entry.
  useEffect(() => {
    if (!open) return;
    setPriceStr("");
    setName("");
    setAutoGen(true);
    setCardCode("");
    setError("");
  }, [open, item?.id]);

  const labels = useMemo(() => {
    switch (kind) {
      case "drink":
        return { title: "Open Drink", nameLabel: "What is it?", namePlaceholder: "e.g. Espresso Martini", priceLabel: "Price ($)" };
      case "gift_card":
        return { title: "Sell Gift Card", nameLabel: "Recipient (optional)", namePlaceholder: "e.g. For Alex", priceLabel: "Amount on the card ($)" };
      default:
        return { title: "Open Food", nameLabel: "What is it?", namePlaceholder: "e.g. Off-menu special", priceLabel: "Price ($)" };
    }
  }, [kind]);

  if (!item) return null;

  const submit = () => {
    setError("");
    const price = parseFloat(priceStr);
    if (!(price >= 0.01)) {
      setError("Enter a price greater than $0.00");
      return;
    }
    if (kind !== "gift_card" && !name.trim()) {
      setError("Enter a description so the ticket and receipt show what this is");
      return;
    }
    const payload = { price, custom_name: name.trim() };
    if (kind === "gift_card") {
      // Blank code + auto-gen ON → backend generates a 16-digit code.
      if (!autoGen && !cardCode.trim()) {
        setError("Scan or type the physical card code (or turn Auto-Generate back on)");
        return;
      }
      payload.gift_card_code = autoGen ? "" : cardCode.trim();
    }
    onConfirm(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="sm:max-w-[420px]" data-testid="open-item-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{labels.title}</DialogTitle>
          <DialogDescription>
            {kind === "gift_card"
              ? "Enter the dollar amount on the card. Turn off Auto-Generate to scan or type a physical card code."
              : "Enter a name and price for this ad-hoc item — it'll show on the ticket, KDS and receipt."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{labels.priceLabel}</Label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              autoFocus
              value={priceStr}
              onChange={(e) => setPriceStr(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              placeholder="0.00"
              data-testid="open-item-price"
            />
          </div>

          <div className="space-y-2">
            <Label>{labels.nameLabel}</Label>
            <Textarea
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={labels.namePlaceholder}
              rows={2}
              data-testid="open-item-name"
            />
          </div>

          {kind === "gift_card" && (
            <div className="space-y-3 rounded-lg border border-dashed border-border p-3 bg-secondary/20">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">Auto-generate card code</p>
                  <p className="text-xs text-muted-foreground">On for e-gift cards. Off = scan / type a physical card.</p>
                </div>
                <Switch checked={autoGen} onCheckedChange={setAutoGen} data-testid="open-item-autogen-toggle" />
              </div>
              {!autoGen && (
                <div className="space-y-1">
                  <Label className="text-xs">Card code</Label>
                  <Input
                    value={cardCode}
                    onChange={(e) => setCardCode(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                    placeholder="Scan or type the code…"
                    className="font-mono"
                    data-testid="open-item-card-code"
                    autoFocus
                  />
                </div>
              )}
            </div>
          )}

          {error && (
            <p className="text-xs text-destructive" data-testid="open-item-error">{error}</p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} data-testid="open-item-cancel">Cancel</Button>
          <Button onClick={submit} data-testid="open-item-confirm">Add to Order</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
